/**
 * BoardTaskDelta — the slim home-board wire (home-streams lane, 07-15).
 *
 * Push-optimized, level-triggered truth: the server emits this to room
 * `user:${userId}` at every board-relevant task write; the web board applies
 * it as a HINT (ordering-guarded by `at`), and a full tasks:list read always
 * wins wholesale. Never widen this with jsonb columns — the whole point is
 * that it stays cheap enough to emit on every write.
 */

import { z } from 'zod';
import type { TaskLifecycleState } from './task.js';

export interface BoardTaskDelta {
  taskId: string;
  /** Server emit time (ISO). Ordering guard: web drops deltas older than the
   *  newest one it has applied for this task. */
  at: string;
  lifecycleState: TaskLifecycleState;
  title: string | null;
  pulseHeadline: string | null;
  pulseHeadlineAt: string | null;
  /** Canonical board sort key; web merges with max(existing, delta). */
  lastActivityAt: string;
  /** Set only on rest-snapshot emits (done-verdict/output-doc writes landed
   *  AFTER the idle delta): a newer value than the web has seen means "the
   *  finished card upgraded — one silent truth read". Never the payload
   *  itself: this wire stays slim by contract. */
  restSnapshotAt?: string | null;
}

export const BoardTaskDeltaSchema = z
  .object({
    taskId: z.string(),
    at: z.string(),
    lifecycleState: z.enum(['scheduled', 'queued', 'idle', 'processing', 'awaiting', 'error', 'completed']),
    title: z.string().nullable(),
    pulseHeadline: z.string().nullable(),
    pulseHeadlineAt: z.string().nullable(),
    lastActivityAt: z.string(),
    restSnapshotAt: z.string().nullable().optional(),
  })
  .strict();
